import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import ProviderQuotaCard from './ProviderQuotaCard';
import QuotaGroupCard from './QuotaGroupCard';
import { QuotaData, groupSharedQuotas } from './quotaUtils';

interface Props {
  provider: string;
  quotas: QuotaData[];
  onRefresh: () => void;
  onRequestIncrease: (quota: QuotaData, groupLabel?: string, groupFamilies?: string[]) => void;
}

const providerLogos: Record<string, string> = {
  aws: '/icons/icons8-aws-96.svg',
  gcp: '/icons/icons8-google-cloud-96.svg',
  azure: '/icons/azure.svg',
};

export default function ProviderQuotaView({ provider, quotas, onRefresh, onRequestIncrease }: Props) {
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [filterUtilization, setFilterUtilization] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Group quotas by region
  const quotasByRegion = quotas.reduce((acc, quota) => {
    if (!acc[quota.region]) {
      acc[quota.region] = [];
    }
    acc[quota.region].push(quota);
    return acc;
  }, {} as Record<string, QuotaData[]>);

  // Calculate region stats using group-aware utilization
  const regionStats = Object.entries(quotasByRegion).map(([region, regionQuotas]) => {
    const { groups, ungrouped } = groupSharedQuotas(provider, regionQuotas);

    // Build a list of effective utilization values (groups + ungrouped)
    const allUtilizations = [
      ...groups.map(g => g.utilization_percent),
      ...ungrouped.map(q => q.utilization_percent),
    ];

    const effectiveCount = allUtilizations.length;
    const critical = allUtilizations.filter(u => u >= 90).length;
    const warning = allUtilizations.filter(u => u >= 80 && u < 90).length;
    const healthy = allUtilizations.filter(u => u < 80).length;
    const avgUtilization = effectiveCount > 0
      ? allUtilizations.reduce((sum, u) => sum + u, 0) / effectiveCount
      : 0;

    // Subtitle info
    const groupCount = groups.length;
    const familyCount = ungrouped.length;

    return {
      region,
      quotas: regionQuotas,
      groups,
      ungrouped,
      critical,
      warning,
      healthy,
      avgUtilization,
      total: effectiveCount,
      groupCount,
      familyCount,
    };
  });

  // Apply filters — filter at the family level, then recompute groups
  const filteredRegionStats = regionStats.map(stat => {
    const filteredQuotas = stat.quotas.filter(
      q =>
        q.utilization_percent >= filterUtilization &&
        (searchTerm === '' ||
          q.instance_family.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.region.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const { groups, ungrouped } = groupSharedQuotas(provider, filteredQuotas);

    return {
      ...stat,
      quotas: filteredQuotas,
      groups,
      ungrouped,
    };
  }).filter(stat => stat.quotas.length > 0);

  const toggleRegion = (region: string) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(region)) {
      newExpanded.delete(region);
    } else {
      newExpanded.add(region);
    }
    setExpandedRegions(newExpanded);
  };

  const expandAll = () => {
    setExpandedRegions(new Set(Object.keys(quotasByRegion)));
  };

  const collapseAll = () => {
    setExpandedRegions(new Set());
  };

  const formatSubtitle = (groupCount: number, familyCount: number) => {
    if (groupCount === 0) return `${familyCount} families`;
    const parts: string[] = [];
    parts.push(`${groupCount} quota group${groupCount !== 1 ? 's' : ''}`);
    if (familyCount > 0) parts.push(`${familyCount} ${familyCount === 1 ? 'family' : 'families'}`);
    return parts.join(', ');
  };

  return (
    <div className="space-y-6">
      {/* Provider Header */}
      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <img
              src={providerLogos[provider]}
              alt={provider}
              className="w-10 h-10 object-contain"
            />
            <div>
              <h2 className="text-2xl font-bold text-white">{provider.toUpperCase()} Quota Details</h2>
              <p className="text-gray-400">
                {quotas.length} resources across {Object.keys(quotasByRegion).length} regions
              </p>
            </div>
          </div>
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Refresh All
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by instance family or region..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-gray-400 text-sm">Min Utilization:</label>
            <input
              type="range"
              min="0"
              max="100"
              value={filterUtilization}
              onChange={(e) => setFilterUtilization(Number(e.target.value))}
              className="w-32"
            />
            <span className="text-white font-medium w-12">{filterUtilization}%</span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={expandAll}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Region Accordions */}
      <div className="space-y-4">
        {filteredRegionStats.map(({ region, quotas: regionQuotas, groups, ungrouped, critical, warning, healthy, avgUtilization, groupCount, familyCount }) => {
          const isExpanded = expandedRegions.has(region);

          return (
            <div key={region} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
              {/* Region Header */}
              <div
                onClick={() => toggleRegion(region)}
                className="p-4 bg-gray-800/50 hover:bg-gray-800/70 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {isExpanded ? (
                      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {regionQuotas[0]?.region_display_name || region}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {formatSubtitle(groupCount, familyCount)} &bull; Avg: {Math.round(avgUtilization)}%
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {/* Status Pills */}
                    {critical > 0 && (
                      <span className="px-2 py-1 bg-red-900/50 text-red-400 text-xs rounded-full">
                        {critical} Critical
                      </span>
                    )}
                    {warning > 0 && (
                      <span className="px-2 py-1 bg-yellow-900/50 text-yellow-400 text-xs rounded-full">
                        {warning} Warning
                      </span>
                    )}
                    {healthy > 0 && (
                      <span className="px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded-full">
                        {healthy} Healthy
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Region Content */}
              {isExpanded && (
                <div className="p-4 space-y-4">
                  {/* Group cards (full-width) */}
                  {groups.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {groups.map((group) => (
                        <QuotaGroupCard
                          key={group.groupKey}
                          group={group}
                          onRequestIncrease={onRequestIncrease}
                        />
                      ))}
                    </div>
                  )}

                  {/* Ungrouped family cards */}
                  {ungrouped.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {ungrouped
                        .sort((a, b) => b.utilization_percent - a.utilization_percent)
                        .map((quota) => (
                          <ProviderQuotaCard
                            key={`${quota.provider}-${quota.region}-${quota.instance_family}`}
                            quota={quota}
                            onRequestIncrease={() => onRequestIncrease(quota)}
                          />
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredRegionStats.length === 0 && (
        <div className="bg-gray-900 rounded-xl p-12 text-center border border-gray-800">
          <p className="text-gray-400">No quotas match your filters</p>
          <p className="text-gray-500 text-sm mt-2">Try adjusting your search or utilization filter</p>
        </div>
      )}
    </div>
  );
}
